import { memo } from 'react'
import { SiShopify } from 'react-icons/si'
import { OAuthButton } from './OAuthButton'

interface ShopifyCredentialsFormProps {
  credentials: Record<string, string>
  isOAuthInProgress: boolean
  onFieldChange: (key: string, value: string) => void
  onShopifyOAuth: () => void
}

export const ShopifyCredentialsForm = memo(function ShopifyCredentialsForm({
  credentials,
  isOAuthInProgress,
  onFieldChange,
  onShopifyOAuth,
}: ShopifyCredentialsFormProps) {
  const isFormValid =
    credentials['shopDomain']?.trim() &&
    credentials['clientId']?.trim() &&
    credentials['clientSecret']?.trim()

  return (
    <>
      <div className="space-y-4">
        <div>
          <label htmlFor="shopDomain" className="mb-1 block text-sm font-medium text-black">
            Shop Domain
          </label>
          <p className="mb-2 text-sm text-black/60">Your Shopify store domain</p>
          <input
            id="shopDomain"
            type="text"
            value={credentials['shopDomain'] || ''}
            onChange={(e) => onFieldChange('shopDomain', e.target.value)}
            placeholder="mystore.myshopify.com"
            className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
            disabled={isOAuthInProgress}
          />
        </div>

        <div>
          <label htmlFor="clientId" className="mb-1 block text-sm font-medium text-black">
            Client ID
          </label>
          <p className="mb-2 text-sm text-black/60">
            Your custom Shopify app&apos;s API key (from Shopify Partner Dashboard)
          </p>
          <input
            id="clientId"
            type="text"
            value={credentials['clientId'] || ''}
            onChange={(e) => onFieldChange('clientId', e.target.value)}
            placeholder="Enter your custom app API key"
            className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
            disabled={isOAuthInProgress}
          />
        </div>

        <div>
          <label htmlFor="clientSecret" className="mb-1 block text-sm font-medium text-black">
            Secret
          </label>
          <p className="mb-2 text-sm text-black/60">
            Your custom Shopify app&apos;s API secret key (from Shopify Partner Dashboard)
          </p>
          <input
            id="clientSecret"
            type="password"
            value={credentials['clientSecret'] || ''}
            onChange={(e) => onFieldChange('clientSecret', e.target.value)}
            placeholder="Enter your custom app API secret"
            className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
            disabled={isOAuthInProgress}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-blue-900">
            Need Help?
          </p>
          <div className="space-y-2">
            <a
              href="https://sprites-ai.notion.site/Sprites-Shopify-Integration-2df5daa5843b8085bbd9cbc63982296f?pvs=73"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-700 transition-colors hover:text-blue-900"
            >
              <span className="text-base">📖</span>
              <span className="underline">
                Setup guide to generate Client ID and Secret for Shopify
              </span>
            </a>
            <a
              href="https://calendly.com/spritesai/sprites-support"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-700 transition-colors hover:text-blue-900"
            >
              <span className="text-base">💬</span>
              <span className="underline">Book a support call</span>
            </a>
          </div>
        </div>

        <OAuthButton
          onClick={onShopifyOAuth}
          disabled={isOAuthInProgress || !isFormValid}
          isLoading={isOAuthInProgress}
          icon={<SiShopify className="h-5 w-5" />}
        >
          {isOAuthInProgress ? 'Connecting...' : 'Connect with Shopify'}
        </OAuthButton>
      </div>
    </>
  )
})
